/**
 * graph.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { ElementRef, Injectable, Renderer } from "@angular/core";
import { Observable } from "rxjs/Rx";

import { AlgorithmStepService }
  from "../algorithms/management/algorithm-step.service";
import { AlgorithmStep, ComputationStep, IntakeStep, ReportStep }
  from "../algorithms/structure";
import { NextStepsResponse } from "../server/messages/algorithms/";
import {
  AbbreviatedEdge,
  ExplanationRequest,
  ExplanationResponse,
  StepDefinitionsRequest
} from "../server/messages/algorithms/graphs/";
import { MessagingService } from "../server/messaging.service";

import { ExplanationGraph, ExplanationNode } from "./explanation-graph";
import { Graph, GraphEdge, GraphNode } from "./graph";
// import { Selectionlike } from "./selectionlike";

@Injectable()
export class GraphService
{
  constructor (
    public messaging : MessagingService,
    public steps : AlgorithmStepService
  ) {}

  public explainStep (step : AlgorithmStep) : Observable<ExplanationGraph>
  {
    return this.messaging.converse(new ExplanationRequest(step.id, true))
      .flatMap((msg : ExplanationResponse) => this.fromMessage(msg));
  }

  private nodeFromStep (step : AlgorithmStep) : ExplanationNode
  {
    const node : ExplanationNode = new ExplanationNode(step.id);
    node.step = step;
    node.label = step.displayName;
    if (step instanceof IntakeStep)
    {
      node.category = "IntakeStep";
      return node;
    }
    if (step instanceof ReportStep)
    {
      node.category = "ReportStep";
      return node;
    }
    if (step instanceof ComputationStep)
    {
      node.category = "ComputationStep";
      return node;
    }

    console.error("Algorithm step is not a known step type:", step);
    throw new Error("Algorithm step is not a known step type!");
  }

  private fromMessage (msg : ExplanationResponse) : Observable<ExplanationGraph>
  {
    const nodeIds : Set<number> = new Set<number>([msg.explanation]);
    msg.edges.forEach((edge : AbbreviatedEdge) => {
      nodeIds.add(edge.source);
      nodeIds.add(edge.target);
    });

    const nodes : ExplanationNode[] = [];
    const unknownNodes : number[] = [];
    nodeIds.forEach((id : number) => {
      const step : AlgorithmStep = this.steps.get(id);
      if (!step)
      {
        return unknownNodes.push(id);
      }
      else
      {
        nodes.push(this.nodeFromStep(step));
      }
    });

    const edgesAwaitingNodes : AbbreviatedEdge[] = [];
    const edges : GraphEdge[] = [];
    msg.edges.forEach(
      (edge : AbbreviatedEdge) => {
        if (unknownNodes.indexOf(edge.source) > -1
            || unknownNodes.indexOf(edge.target) > -1)
        {
          edgesAwaitingNodes.push(edge);
        }
        else
        {
          const source : ExplanationNode = nodes.find(
            (n : ExplanationNode) => n.id === edge.source);
          const target : ExplanationNode = nodes.find(
            (n : ExplanationNode) => n.id === edge.target);
          if (!source || !target)
          {
            throw new Error("Claimed node ID was known, but could not resolve");
          }
          edges.push(new GraphEdge(source, target));
        }
    });

    if (unknownNodes.length > 0)
    {
      return this.steps.requestDefinitions(unknownNodes)
        .map((steps : AlgorithmStep[]) => {
          steps.forEach(
            (step : AlgorithmStep) => nodes.push(this.nodeFromStep(step))
          );
          edgesAwaitingNodes.forEach(
            (edge : AbbreviatedEdge) => {
              const source : ExplanationNode = nodes.find(
                (n : ExplanationNode) => n.id === edge.source);
              const target : ExplanationNode = nodes.find(
                (n : ExplanationNode) => n.id === edge.target);
              if (!source || !target)
              {
                console.warn("Could not find node for edge!", edge);
                throw new Error("Could not resolve node after dfn request!");
              }
              edges.push(new GraphEdge(source, target));
          });
          return new ExplanationGraph(nodes, edges);
        });
    }
    else
    {
      return Observable.of(new ExplanationGraph(nodes, edges));
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/graphs/graph.service.ts